import { concat } from '@ember/helper';
import { eq } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import DefinitionList from '../../ui/definition-list.gts';
import safeFormatDate from '../../../helpers/safe-format-date.ts';
import type IntlService from 'ember-intl/services/intl';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TASProgramInstanceModel from '../../../models/tas-program-instance.ts';
import { Section } from 'tio-ui/components/layout';

interface TasAppDetailsEmployeeEligibilitySig {
  Args: {
    programInstance: TASProgramInstanceModel;
  };
}

type Period = [string, string]; // Each period is a tuple of two dates
type PeriodWithStatus = {
  start: string;
  end: string;
  status: 'Eligible' | 'Ineligible';
};

export default class TasAppDetailsEmployeeEligibility extends Component<TasAppDetailsEmployeeEligibilitySig> {
  @service declare intl: IntlService;
  @service declare store: StoreService;

  incrementDate(date: string) {
    const d = new Date(date);
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  }

  decrementDate(date: string) {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  }

  periodsofEligibilityData = trackedFunction(this, async () => {
    try {
      const data = await this.store
        .adapterFor('tas-participant')
        // @ts-expect-error - Legacy adapter method, will be migrated
        .periodOfEligibility(
          this.args.programInstance.employee.tasParticipant.id,
          this.args.programInstance.tasProgramTemplate.code
        );
      return data;
    } catch (e) {
      console.warn(e);
    }
  });

  get periodsofEligibility() {
    return this.periodsofEligibilityData?.value || [];
  }

  get formattedPeriodsOfEligibility(): PeriodWithStatus[] {
    return this.periodsofEligibility.reduce(
      (results: PeriodWithStatus[], period: Period, index: number, periods: Period[]) => {
        const [currentStart, currentEnd] = period;

        // Add the eligible period
        results.push({
          start: currentStart,
          end: currentEnd,
          status: 'Eligible',
        });

        // Check if there's a next period and calculate the ineligible gap
        const nextPeriod = periods[index + 1];
        if (nextPeriod) {
          const nextStart = nextPeriod[0];
          const gapStart = this.incrementDate(currentEnd) as string;
          const gapEnd = this.decrementDate(nextStart) as string;

          if (new Date(gapEnd) >= new Date(gapStart)) {
            results.push({
              start: gapStart,
              end: gapEnd,
              status: 'Ineligible',
            });
          }
        }

        return results;
      },
      []
    );
  }

  get lockedOnText() {
    const lock = this.args.programInstance.tasProgramTemplate.lockEmployeeStatusBasedOn;

    const lockMapping: Record<string, string> = {
      ON_COURSES_SUBMISSION: this.intl.t(
        'common.tuition_assistance.employee_eligibility.on_courses_submission'
      ),
      FULFILLED_DATE: this.intl.t('common.tuition_assistance.employee_eligibility.fulfilled_date'),
      COURSE_START_DATE: this.intl.t(
        'common.tuition_assistance.employee_eligibility.course_start_date'
      ),
      COURSE_END_DATE: this.intl.t(
        'common.tuition_assistance.employee_eligibility.course_end_date'
      ),
    };

    return lockMapping[lock] || lock;
  }

  <template>
    <Section>
      <:header>
        {{t "common.tuition_assistance.employee_eligibility.default"}}
      </:header>
      <:body>
        <DefinitionList class="mb-2" as |options|>
          <div class="px-0 border-b border-gray-100 {{options.wrappingClass}}">
            <options.dt>{{t
                "common.tuition_assistance.employee_eligibility.locked_on"
              }}</options.dt>
            <options.dd>{{this.lockedOnText}}</options.dd>
          </div>
        </DefinitionList>

        <h3 class="uppercase font-medium text-tio-gray-700 text-sm">
          {{t "common.tuition_assistance.employee_eligibility.eligibility"}}
        </h3>
        <DefinitionList as |options|>
          {{#each this.formattedPeriodsOfEligibility as |period|}}
            <div
              class={{if
                (eq period.status "Eligible")
                options.wrappingClass
                (concat options.wrappingClass " bg-red-50 -mx-2 px-2")
              }}
            >
              <options.dd class={{if (eq period.status "Eligible") "" "p[&>div]:text-red-800"}}>
                {{safeFormatDate period.start}}
                -
                {{safeFormatDate period.end}}
              </options.dd>
              <options.dt class={{if (eq period.status "Eligible") "" " !text-red-800"}}>
                {{period.status}}
              </options.dt>
            </div>
          {{/each}}
        </DefinitionList>
      </:body>
    </Section>
  </template>
}
